"use client";
import { usePassportProvider } from "@/context";
import { cn } from "@/lib/utils";
import { X, XCircle } from "lucide-react";
import { OnboardingFlow } from "../OnboardingFlow";
import { OnboardingDotStepper } from "./OnboardingDotStepper";
import { OnboardingSignUp } from "./OnboardingSignUp";

interface OnboardingContentProps {
  /** Title override for sign up screen */
  signUpTitle?: string;
  /** Whether this is being used in a modal context */
  isModal?: boolean;
  /** Handler for closing the modal (only for modal) */
  handleCloseModal?: () => void;
}

/**
 * Shared onboarding content used in both the modal and the page.
 * Contains the same styling and structure for consistency.
 * Automatically handles login state to show either sign up or flow.
 */
export function OnboardingContent({
  signUpTitle,
  isModal = false,
  handleCloseModal,
}: OnboardingContentProps) {
  const { isLoggedIn: isWalletLoggedIn, isLoggingIn } = usePassportProvider();

  // Determine if we should show the onboarding flow (logged in) or sign up (logged out)
  const showOnboardingFlow =
    isWalletLoggedIn !== undefined && isWalletLoggedIn === true;

  // Render close button for modal context
  const closeButton =
    isModal && handleCloseModal ? (
      showOnboardingFlow ? (
        <button type="button" disabled={isLoggingIn} onClick={handleCloseModal}>
          <XCircle className="size-6" stroke="#B6B6B6" />
        </button>
      ) : (
        <button
          type="button"
          disabled={isLoggingIn}
          onClick={handleCloseModal}
          className="absolute top-4 right-4 p-2 z-10 bg-black/50 backdrop-blur-md border-2 border-white rounded-full"
        >
          <X className="size-4" stroke="white" />
        </button>
      )
    ) : null;

  return (
    <>
      {showOnboardingFlow ? (
        <div
          className={cn(
            "bg-[#0d0d0d] overflow-hidden gap-y-4 flex flex-col shadow-xl",
            isModal
              ? "rounded-2xl p-2 pt-4 pb-2 h-full w-full"
              : "rounded-none md:rounded-2xl p-2 md:pt-4 md:pb-2 w-full h-full",
          )}
        >
          <div
            className={cn(
              "items-center justify-between w-full px-4 flex-shrink-0",
              isModal ? "flex" : "hidden md:flex",
            )}
          >
            <div className="size-9" />
            <OnboardingDotStepper />
            {closeButton || <div className="size-9" />}
          </div>
          <div className="flex-1 min-h-0">
            <OnboardingFlow isModal={isModal} />
          </div>
        </div>
      ) : (
        <>
          {closeButton}
          <OnboardingSignUp title={signUpTitle} />
        </>
      )}
    </>
  );
}
